import {
  DailyEventObjectMeetingSessionStateUpdated,
  DailyMeetingSessionState,
} from '@daily-co/daily-js';
import { useDaily, useDailyEvent } from '@daily-co/daily-react';
import { useEffect } from 'react';
import { atom, useRecoilCallback, useRecoilValue } from 'recoil';

const meetingSessionDataState = atom<DailyMeetingSessionState>({
  key: 'meeting-session-data',
  default: undefined,
});

export const useMeetingSessionState = <T = any>() => {
  const daily = useDaily();
  const meetingSessionState = useRecoilValue(meetingSessionDataState);

  const initState = useRecoilCallback(
    ({ set }) =>
      () => {
        set(meetingSessionDataState, daily.meetingSessionState());
      },
    [daily]
  );

  /**
   * Initialize state when joined meeting or setting up the hook.
   */
  useDailyEvent('joined-meeting', initState);
  useEffect(() => {
    if (daily?.meetingState() !== 'joined-meeting') return;
    initState();
  }, [daily, initState]);

  /**
   * Update Recoil state whenever meeting session state is updated.
   */
  useDailyEvent(
    'meeting-session-state-updated',
    useRecoilCallback(
      ({ set }) =>
        (ev: DailyEventObjectMeetingSessionStateUpdated) => {
          set(meetingSessionDataState, ev.meetingSessionState);
        },
      []
    )
  );

  /**
   * Reset Recoil state when meeting ends.
   */
  useDailyEvent(
    'left-meeting',
    useRecoilCallback(
      ({ reset }) =>
        () => {
          reset(meetingSessionDataState);
        },
      []
    )
  );

  return {
    data: meetingSessionState?.data as T,
    topology: meetingSessionState?.topology,
  };
};
